import React, { useState, useEffect, useRef } from 'react';
import type { Goal } from '../types';

interface ShotPositionDiagramProps {
  onPositionSelect: (x: number, y: number) => void;
  goals?: Goal[];
  width?: number;
  height?: number;
  selectedGoal?: Goal | null;
  readOnly?: boolean;
  provisionalPosition?: { x: number; y: number } | null;
  hideNumbers?: boolean;
}

export default function ShotPositionDiagram({ 
  onPositionSelect, 
  goals = [], 
  width = 400, 
  height = 300,
  selectedGoal,
  readOnly = false,
  provisionalPosition = null,
  hideNumbers = false
}: ShotPositionDiagramProps) {
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
  const [diagramSize, setDiagramSize] = useState({ width, height });
  const svgRef = useRef<SVGSVGElement>(null);

  // Adjust diagram size for mobile
  useEffect(() => {
    const updateSize = () => {
      if (window.innerWidth < 768) {
        const containerWidth = Math.min(window.innerWidth - 64, width);
        const aspectRatio = height / width;
        const containerHeight = containerWidth * aspectRatio;
        setDiagramSize({ 
          width: containerWidth, 
          height: containerHeight 
        });
      } else {
        setDiagramSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [width, height]);

  // Convert coordinates to percentage positions using SVG coordinate system
  const toPercentage = (clientX: number, clientY: number, svgElement: SVGSVGElement) => {
    // Get the SVG point using the proper coordinate transformation
    const point = svgElement.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    
    // Transform the point to SVG coordinates with null check
    const matrix = svgElement.getScreenCTM();
    if (!matrix) {
      // Fallback to center if matrix is unavailable
      return { x: 50, y: 50 };
    }
    
    const svgPoint = point.matrixTransform(matrix.inverse());
    
    return {
      x: Math.min(100, Math.max(0, svgPoint.x)),
      y: Math.min(100, Math.max(0, svgPoint.y))
    };
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (readOnly) return;
    const { x, y } = toPercentage(e.clientX, e.clientY, e.currentTarget);
    setHoverPosition({ x, y });
  };

  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (readOnly) return;
    const { x, y } = toPercentage(e.clientX, e.clientY, e.currentTarget);
    onPositionSelect(x, y);
  };

  // Handle touch events with proper preventDefault
  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement || readOnly) return;

    let touchStartPosition: { x: number; y: number } | null = null;
    let hasMoved = false;

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      if (touch) {
        const { x, y } = toPercentage(touch.clientX, touch.clientY, svgElement);
        touchStartPosition = { x, y };
        hasMoved = false;
        setHoverPosition({ x, y });
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      if (touch) {
        hasMoved = true;
        const { x, y } = toPercentage(touch.clientX, touch.clientY, svgElement);
        setHoverPosition({ x, y });
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      if (touch) {
        const { x, y } = toPercentage(touch.clientX, touch.clientY, svgElement);
        
        // Use final touch position for selection
        onPositionSelect(x, y);
        setHoverPosition(null);
      }
      
      touchStartPosition = null;
      hasMoved = false;
    };

    // Add with { passive: false } to allow preventDefault
    svgElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    svgElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    svgElement.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      svgElement.removeEventListener('touchstart', handleTouchStart);
      svgElement.removeEventListener('touchmove', handleTouchMove);
      svgElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [readOnly, onPositionSelect]);

  return (
    <div className="flex justify-center items-center w-full">
      <svg
        ref={svgRef}
        width={diagramSize.width}
        height={diagramSize.height}
        viewBox="0 0 100 100"
        className={`border-4 border-[#1b3968] rounded-lg ${
          !readOnly ? 'cursor-crosshair touch-none select-none' : ''
        }`}
        style={{
          touchAction: readOnly ? 'auto' : 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none'
        }}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
        onMouseLeave={() => setHoverPosition(null)}
      >
        {/* Background Image */}
        <image
          href="https://cdn.shopify.com/s/files/1/0609/6397/9463/files/Half_hockey_rink.png?v=1743095801"
          x="0"
          y="0"
          width="100"
          height="100"
          preserveAspectRatio="none"
        />

        {/* Existing shot positions */}
        {goals.map((goal, index) => {
          if (!goal.shotXPosition || !goal.shotYPosition) return null;
          return (
            <g key={goal.id}>
              <circle
                cx={goal.shotXPosition}
                cy={goal.shotYPosition}
                r='1.5'
                fill={goal.id === selectedGoal?.id ? '#1b3968' : '#ef4444'}
                className=""
              />
              {goal.id === selectedGoal?.id && (
                <circle
                  cx={goal.shotXPosition}
                  cy={goal.shotYPosition}
                  r='3'
                  fill='none'
                  stroke='#1b3968'
                  strokeWidth='1'
                  className=''
                />
              )}
              {!hideNumbers && (
                <text
                  x={goal.shotXPosition + 2}
                  y={goal.shotYPosition - 2}
                  className='text-xs fill-gray-700'
                  style={{ fontSize: '8px' }}
                >
                  {index + 1}
                </text>
              )}
            </g>
          );
        })}

        {/* Provisional position */}
        {provisionalPosition && (
          <g>
            <circle
              cx={provisionalPosition.x}
              cy={provisionalPosition.y}
              r="2.5"
              fill="#3b82f6"
              className=""
            />
            <circle
              cx={provisionalPosition.x}
              cy={provisionalPosition.y}
              r="5"
              fill="none"
              stroke="#3b82f6"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
          </g>
        )}

        {/* Hover indicator */}
        {!readOnly && hoverPosition && !provisionalPosition && (
          <g>
            <circle
              cx={hoverPosition.x}
              cy={hoverPosition.y}
              r="2.5"
              fill="rgba(59, 130, 246, 0.5)"
            />
            <line
              x1="0"
              y1={hoverPosition.y}
              x2="100"
              y2={hoverPosition.y}
              stroke="rgba(59, 130, 246, 0.2)"
              strokeDasharray="2,2"
            />
            <line
              x1={hoverPosition.x}
              y1="0"
              x2={hoverPosition.x}
              y2="100"
              stroke="rgba(59, 130, 246, 0.2)"
              strokeDasharray="2,2"
            />
          </g>
        )}
      </svg>
    </div>
  );
}